import './EditionPage.scss';
import { Button, Card, Grid, Icon, LabelValue, Separator, Typography } from '@design-system/index';
import { CustomersListLine, RoundedArrow } from '@icons';
import { formatDate } from '@utils/dates';
import { useEditionPageVM } from './useEditionPageVM';
import { useNavigate } from 'react-router-dom';
import { GENDER, PAYMENT_TYPE } from '@utils/enumTranslation';
import { Contribution, MemberTable } from '@services/member/member/member.types';
import { useGetMembersMutation, useGetMemberMutation } from '@services/member/member/member.endpoints';
import { AsyncDropdown } from '@design-system/components/Dropdown/AsyncDropdown';
import { Table } from '@components/Table/Table/Table';
import { ColumnDef } from '@tanstack/react-table';
import { formatMoney } from '@utils/numbers';
import moment from 'moment';

export default function EditionPage() {
    const { memberValidation, foundMember, isLoading, selectedMember, setSelectedMember, handleArchive, handleActivation, contributionColumnHelper } =
        useEditionPageVM();
    const navigate = useNavigate();
    const [getMember] = useGetMemberMutation();

    const contributionsColumns = [
        contributionColumnHelper.accessor('year', {
            header: 'Année',
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => <div style={{ display: 'flex', justifyContent: 'center' }}>{info.getValue()}</div>
        }),
        contributionColumnHelper.accessor('dateContributed', {
            header: 'Date de paiement',
            cell: (info) => {
                const value = info.getValue();
                return <>{value ? moment(value).format('DD/MM/YYYY') : ''}</>;
            }
        }),
        contributionColumnHelper.accessor('amount', {
            header: 'Montant',
            cell: (info) => <>{formatMoney(info.getValue())} </>
        }),
        contributionColumnHelper.accessor('paymentType', {
            header: 'Nature',
            cell: (info) => <>{PAYMENT_TYPE[info.getValue()]} </>
        }),
        contributionColumnHelper.accessor('observation', {
            header: 'Commentaire',
            cell: (info) => <>{info.getValue()} </>
        })
    ] as ColumnDef<Contribution>[];

    return (
        <div className="members-validation-container">
            <Card
                isLoading={isLoading}
                headerType="full"
                showBack
                headerTitle={"Informations sur l'adhérent à valider"}
                headerIcon={<Icon icon={<CustomersListLine />} size={20} color="primary" />}
            >
                <Card showHeader={false} headerAction={<Button variant="icon-hover" color="grey-secondary" />}>
                    <Typography intrinsic="h3" className="card-title">
                        Informations du formulaire
                    </Typography>
                    <Grid columns={4}>
                        <LabelValue label="Genre" value={memberValidation?.gender} />
                        <LabelValue label="Nom" value={memberValidation?.lastname} />
                        <LabelValue label="Prénom" value={memberValidation?.firstname} />
                        <LabelValue label="N°COSEM" value={memberValidation?.cosemNumber} />
                        <LabelValue label="Date de naissance" value={formatDate(memberValidation?.birthDate ?? '')} />
                        <LabelValue label="Statut" value={memberValidation?.status} />
                        <LabelValue label="Email" value={memberValidation?.email} />
                        <LabelValue label="Téléphone" value={memberValidation?.phone} />
                    </Grid>
                </Card>
                <Separator />
                {!isLoading && (
                    <>
                        <Card
                            showHeader={!foundMember ? false : true}
                            className={!foundMember ? 'error-card' : 'validation-card'}
                            headerType={!foundMember ? 'none' : 'very-light'}
                            headerAction={
                                foundMember && (
                                    <Button
                                        variant="icon-hover"
                                        color="grey-secondary"
                                        icon={<Icon icon={<RoundedArrow />} size={24} />}
                                        onClick={() => navigate(`/members-management/members/${foundMember?.id}/details/profile`)}
                                    />
                                )
                            }
                        >
                            {!foundMember ? (
                                <>
                                    <Typography intrinsic="h3" className="card-title">
                                        Aucun correspondance d'adhérent trouvée
                                    </Typography>
                                    <Typography intrinsic="p">
                                        Malheureusement, aucun adhérent n'a été trouvé avec les informations fournies dans le formulaire.
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <div className="card-title-container">
                                        <Typography intrinsic="h3" className="card-title">
                                            Correspondance trouvée
                                        </Typography>
                                        {foundMember.wordpressId && (
                                            <span className="card-title-container-status">Accès adhérent déjà créé</span>
                                        )}
                                    </div>
                                    <Grid columns={4}>
                                        <LabelValue label="Genre" value={GENDER[foundMember?.personalInformation.gender ?? 'other']} />
                                        <LabelValue label="Nom" value={foundMember?.personalInformation.lastname} />
                                        <LabelValue label="Prénom" value={foundMember?.personalInformation.firstname} />
                                        <LabelValue label="N°COSEM" value={foundMember?.cosemNumber} />
                                        <LabelValue
                                            label="Date de naissance"
                                            value={formatDate(foundMember?.personalInformation.birthDate ?? '')}
                                        />
                                        <LabelValue label="Email" value={foundMember?.contactInformation.email} />
                                        <LabelValue label="Téléphone" value={foundMember?.contactInformation.phone} />
                                    </Grid>
                                </>
                            )}
                        </Card>
                        {foundMember && foundMember?.lastContributions.length > 0 && (
                            <Card
                                headerTitle="Dernières cotisations"
                                headerAction={
                                    <Button
                                        variant="icon-hover"
                                        color="grey-secondary"
                                        icon={<Icon icon={<RoundedArrow />} size={24} />}
                                        onClick={() => navigate(`/members-management/members/${foundMember?.id}/details/contributions`)}
                                    />
                                }
                            >
                                {foundMember.lastContributions.length > 0 ? (
                                    <Table data={foundMember.lastContributions} columns={contributionsColumns} />
                                ) : (
                                    <Typography intrinsic="p" color="secondary">
                                        Aucune cotisation
                                    </Typography>
                                )}
                            </Card>
                        )}
                    </>
                )}
                <Separator large />
                <Typography intrinsic="h3">Accès site web adhérent à créer</Typography>
                <div className="access-web-container">
                    <AsyncDropdown<MemberTable>
                        label="Rechercher un adhérent"
                        required
                        onOptionChange={async (o) => {
                            if (o && !Array.isArray(o)) {
                                const response = await getMember({ id: o.id });
                                if (response.data?.data) {
                                    setSelectedMember(response.data.data);
                                }
                            } else {
                                setSelectedMember(undefined);
                            }
                        }}
                        mutationHook={useGetMembersMutation}
                        mapDataToOptions={(data) =>
                            data.map((item) => ({
                                id: item.id.toString(),
                                label:
                                    item.cosemNumber + ' - ' + item.personalInformation.lastname + ' ' + item.personalInformation.firstname
                            }))
                        }
                        value={
                            selectedMember
                                ? {
                                    id: selectedMember.id.toString(),
                                    label:
                                        selectedMember.cosemNumber +
                                        ' - ' +
                                        selectedMember.personalInformation.lastname +
                                        ' ' +
                                        selectedMember.personalInformation.firstname
                                }
                                : undefined
                        }
                        isSearchable
                    />
                    <Button
                        variant="primary"
                        onClick={() => memberValidation && handleActivation(memberValidation)}
                        disabled={!selectedMember}
                    >
                        {selectedMember?.wordpressId ? 'Réinitialiser le mot de passe' : "Activer l'accès site web"}
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => memberValidation && handleArchive(memberValidation)}
                        disabled={!memberValidation}
                    >
                        Archiver la demande
                    </Button>
                </div>
            </Card>
        </div>
    );
}
